import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import LimeSpinner from "@/components/quote/LimeSpinner";
import { useAddressLookup } from "@/api/hooks";
import type { AddressLookupResult } from "@/api/types";
import {
  trackAddressLookupNotFound,
  trackAddressLookupSearch,
  trackAddressLookupSelect,
} from "@/analytics";
import { addressLookupToFormFields, type UkAddressParts } from "@/utils/quoteMapper";
import { isValidUkPostcode } from "@/utils/ukPostcode";

const LOOKUP_DEBOUNCE_MS = 400;

function addressKey(address: AddressLookupResult) {
  return `${address.line1}-${address.postcode}-${address.label}`;
}

type PostcodeAddressLookupProps = {
  tone?: "light" | "dark" | "powlo";
  submitLabel?: string;
  inputId?: string;
  onAddressSelected: (fields: UkAddressParts) => void;
  onManualEntry?: () => void;
  selectMode?: "immediate" | "confirm";
};

export default function PostcodeAddressLookup({
  tone = "dark",
  submitLabel = "Find address",
  inputId = "postcode-address-lookup",
  onAddressSelected,
  onManualEntry,
  selectMode = "immediate",
}: PostcodeAddressLookupProps) {
  const [postcode, setPostcode] = useState("");
  const [results, setResults] = useState<AddressLookupResult[] | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selected, setSelected] = useState<AddressLookupResult | null>(null);
  const { mutateAsync } = useAddressLookup();
  const requestIdRef = useRef(0);
  const lastRequestedRef = useRef("");
  const inFlightRef = useRef<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const trimmedPostcode = postcode.trim();
  const postcodeValid = isValidUkPostcode(trimmedPostcode);
  const isLight = tone === "light";
  const isPowlo = tone === "powlo";
  const isConfirm = selectMode === "confirm";

  const inputClass = isPowlo
    ? "min-h-[56px] flex-1 rounded-2xl border-2 px-5 text-lg text-ink outline-none"
    : isLight
      ? "flex-1 min-w-[200px] rounded-full border border-[rgba(17,17,17,0.14)] bg-white px-[18px] py-[15px] min-h-[44px] text-[14.5px] text-[#111111] placeholder:text-[#666666]/50 focus:outline-none focus:border-[#D4FF00]/70 transition-colors"
      : "mt-2 min-h-[44px] text-base bg-white/5 border-white/10 text-white placeholder:text-white/30 w-full rounded-lg border px-4 py-3 focus:outline-none focus:border-[#D4FF00]/70";

  const buttonClass = isPowlo
    ? "inline-flex min-h-[56px] shrink-0 items-center justify-center rounded-2xl bg-lime px-6 font-display text-lg font-bold tracking-tight text-ink disabled:cursor-not-allowed disabled:opacity-50"
    : isLight
      ? "inline-flex items-center justify-center rounded-full bg-[#D4FF00] text-[#061525] px-6 py-[15px] min-h-[44px] text-sm font-medium whitespace-nowrap hover:brightness-95 transition-[filter] disabled:opacity-50 disabled:cursor-not-allowed"
      : "mt-4 w-full min-h-[44px] btn-cta btn-cta-lime disabled:opacity-50";

  const formClass = isPowlo
    ? "flex flex-col gap-2 sm:flex-row"
    : isLight
      ? "flex flex-wrap gap-[10px]"
      : "text-left";

  const mutedClass = isPowlo ? "text-muted" : isLight ? "text-[#666666]" : "text-white/50";
  const errorBoxClass = isPowlo
    ? "rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
    : isLight
      ? "rounded-lg px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200"
      : "rounded-lg px-3 py-2 text-sm bg-red-500/10 text-red-300 border border-red-500/30";
  const emptyBoxClass = isPowlo
    ? "rounded-2xl border border-border bg-cream px-4 py-3 text-sm text-muted"
    : isLight
      ? "rounded-lg px-3 py-2 text-sm bg-[#EFEDE8] text-[#666666]"
      : "rounded-lg px-3 py-2 text-sm bg-white/5 text-white/60 border border-white/10";
  const suggestionClass = isPowlo
    ? "rounded-full border-2 border-border bg-white px-3 py-1 text-xs font-semibold text-ink"
    : isLight
      ? "rounded-full px-3 py-1 text-xs font-medium bg-white border border-[rgba(17,17,17,0.14)] text-[#111111] hover:border-[#D4FF00]/70"
      : "rounded-full px-3 py-1 text-xs font-medium bg-white/10 text-white hover:bg-white/15";
  const listClass = isPowlo
    ? "max-h-48 overflow-y-auto rounded-2xl border-2 border-border divide-y divide-border bg-white"
    : isLight
      ? "max-h-48 overflow-y-auto rounded-lg border divide-y border-[rgba(17,17,17,0.14)] divide-[rgba(17,17,17,0.08)] bg-white"
      : "max-h-48 overflow-y-auto rounded-lg border divide-y border-white/10 divide-white/10 bg-[#0d1b2e]";
  const optionTextClass = isPowlo ? "text-ink" : isLight ? "text-[#111111]" : "text-white";

  const invalidateInFlight = () => {
    requestIdRef.current += 1;
    lastRequestedRef.current = "";
    inFlightRef.current = null;
    setIsLookingUp(false);
    setLookupFailed(false);
    setLookupError(null);
  };

  const resetLookupState = () => {
    setResults(null);
    setSuggestions([]);
    setSelected(null);
  };

  const runLookup = useCallback(
    async (value: string) => {
      const normalised = value.trim().toUpperCase();
      if (!isValidUkPostcode(normalised)) return;
      if (inFlightRef.current === normalised) return;

      const requestId = ++requestIdRef.current;
      lastRequestedRef.current = normalised;
      inFlightRef.current = normalised;
      setResults(null);
      setSuggestions([]);
      setSelected(null);
      setLookupFailed(false);
      setLookupError(null);
      setIsLookingUp(true);
      trackAddressLookupSearch(normalised);

      try {
        const data = await mutateAsync(normalised);
        if (requestId !== requestIdRef.current) return;
        setResults(data.addresses);
        setSuggestions(data.suggestions ?? []);
        if (!data.addresses.length) {
          trackAddressLookupNotFound(normalised);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        setResults([]);
        setSuggestions([]);
        setLookupFailed(true);
        setLookupError(
          error instanceof Error && error.message.includes("credits exhausted")
            ? "Address lookup is temporarily unavailable. Please enter your address manually or try again later."
            : null,
        );
      } finally {
        if (requestId === requestIdRef.current) {
          inFlightRef.current = null;
          setIsLookingUp(false);
        }
      }
    },
    [mutateAsync],
  );

  useEffect(() => {
    if (!postcodeValid) return;
    const normalised = trimmedPostcode.toUpperCase();
    if (normalised === lastRequestedRef.current) return;

    const timer = window.setTimeout(() => {
      void runLookup(normalised);
    }, LOOKUP_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmedPostcode, postcodeValid, runLookup]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isConfirm) {
      if (selected) {
        onAddressSelected(addressLookupToFormFields(selected));
        return;
      }
      if (postcodeValid) {
        void runLookup(trimmedPostcode);
      }
      return;
    }
    void runLookup(trimmedPostcode);
  };

  const handleSelect = (address: AddressLookupResult) => {
    trackAddressLookupSelect(trimmedPostcode.toUpperCase());
    if (isConfirm) {
      setSelected(address);
      return;
    }
    onAddressSelected(addressLookupToFormFields(address));
  };

  const handleSuggestionClick = (suggestion: string) => {
    const next = suggestion.toUpperCase();
    invalidateInFlight();
    resetLookupState();
    setPostcode(next);
    void runLookup(next);
  };

  const submitDisabled = isConfirm ? !selected : !postcodeValid;
  const showButtonPending = isLookingUp && !isConfirm;

  return (
    <div className="space-y-3">
      <form onSubmit={handleSubmit} className={formClass}>
        {tone === "dark" && (
          <label htmlFor={inputId} className="text-sm text-white/70">
            Postcode
          </label>
        )}
        <div className={isLight || isPowlo ? "contents" : "mt-2"}>
          <input
            id={inputId}
            type="text"
            name="postcode"
            inputMode="text"
            autoComplete="postal-code"
            placeholder={isPowlo ? "Your postcode" : "e.g. SW1A 1AA"}
            value={postcode}
            onChange={(e) => {
              setPostcode(e.target.value.toUpperCase());
              invalidateInFlight();
              resetLookupState();
            }}
            className={inputClass}
            maxLength={8}
          />
        </div>
        {/* <button
          type="submit"
          disabled={submitDisabled}
          className={buttonClass}
        >
          {showButtonPending ? (
            <>
              <LimeSpinner size="sm" color="current" className="mr-2" decorative />
              Finding addresses…
            </>
          ) : (
            submitLabel
          )}
        </button> */}
      </form>

      {isConfirm && isLookingUp && (
        <p className={`text-xs flex items-center gap-2 ${mutedClass}`}>
          <LimeSpinner size="sm" color="current" decorative />
          Finding addresses…
        </p>
      )}

      {lookupFailed && (
        <div className={errorBoxClass}>
          <p>{lookupError ?? "Could not look up addresses. Please try again."}</p>
          {onManualEntry && (
            <button
              type="button"
              onClick={onManualEntry}
              className={`mt-2 underline ${isPowlo || isLight ? "text-red-800" : "text-red-200"}`}
            >
              Enter address manually
            </button>
          )}
        </div>
      )}

      {results !== null && results.length === 0 && !isLookingUp && !lookupFailed && (
        <div className={emptyBoxClass}>
          <p>No addresses found for this postcode.</p>
          {suggestions.length > 0 && (
            <div className="mt-2">
              <p className="text-xs mb-1">Did you mean:</p>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => handleSuggestionClick(s)}
                    className={suggestionClass}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {onManualEntry && (
            <button
              type="button"
              onClick={onManualEntry}
              className={`mt-2 underline ${isPowlo ? "text-ink" : isLight ? "text-[#111111]" : "text-white/70"}`}
            >
              Enter address manually
            </button>
          )}
        </div>
      )}

      {results !== null && results.length > 0 && (
        <div>
          <p className={`text-xs mb-2 ${mutedClass}`}>
            Select your address ({results.length} found)
          </p>
          <ul
            className={listClass}
            role="listbox"
            aria-label="Addresses at this postcode"
          >
            {results.map((address) => {
              const isSelected = selected != null && addressKey(selected) === addressKey(address);
              return (
                <li key={addressKey(address)}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(address)}
                    className={`w-full text-left px-4 py-3 min-h-[44px] text-sm transition-colors ${optionTextClass} ${
                      isSelected ? "bg-[#D4FF00]/15" : "hover:bg-[#D4FF00]/10"
                    }`}
                  >
                    {address.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
